import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, FileUp, Info, CheckCircle2 } from "lucide-react";

const FIELD_LABELS = [
  { key: "tanggal", label: "Tanggal *", required: true },
  { key: "keterangan", label: "Keterangan / Uraian" },
  { key: "debit", label: "Debit (uang KELUAR)" },
  { key: "kredit", label: "Kredit (uang MASUK)" },
  { key: "jumlah", label: "Jumlah tunggal (+ masuk / − keluar)" },
];

export default function ImporMutasi() {
  const [akun, setAkun] = useState([]);
  const [kategori, setKategori] = useState([]);
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({ tanggal: "", keterangan: "", debit: "", kredit: "", jumlah: "" });
  const [akunId, setAkunId] = useState("");
  const [katMasuk, setKatMasuk] = useState("");
  const [katKeluar, setKatKeluar] = useState("");
  const [jenis, setJenis] = useState("lainnya");
  const [perlakuan, setPerlakuan] = useState("bukan_objek");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/akun").then((r) => { setAkun(r.data); if (r.data[0]) setAkunId(r.data[0].id); });
    api.get("/kategori").then((r) => setKategori(r.data));
  }, []);

  const katMasukList = kategori.filter((k) => k.tipe === "pemasukan");
  const katKeluarList = kategori.filter((k) => k.tipe === "pengeluaran");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const doPreview = async () => {
    if (!csvText.trim()) { toast.error("Pilih file CSV terlebih dahulu"); return; }
    try {
      const r = await api.post("/import/mutasi/preview", { csv_text: csvText });
      setPreview(r.data);
      // auto-guess mapping dari nama header
      const guess = { tanggal: "", keterangan: "", debit: "", kredit: "", jumlah: "" };
      r.data.headers.forEach((h, i) => {
        const s = String(h).toLowerCase();
        if (/tgl|tanggal|date/.test(s) && guess.tanggal === "") guess.tanggal = String(i);
        else if (/ket|uraian|desc|transaksi|remark/.test(s) && guess.keterangan === "") guess.keterangan = String(i);
        else if (/debit|debet|keluar|db/.test(s) && guess.debit === "") guess.debit = String(i);
        else if (/kredit|credit|masuk|cr/.test(s) && guess.kredit === "") guess.kredit = String(i);
        else if (/jumlah|nominal|amount|mutasi/.test(s) && guess.jumlah === "") guess.jumlah = String(i);
      });
      setMapping(guess);
      toast.success(`Terbaca ${r.data.total_rows} baris data`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async () => {
    if (!akunId) { toast.error("Pilih akun/dompet tujuan"); return; }
    if (mapping.tanggal === "") { toast.error("Kolom Tanggal wajib dipetakan"); return; }
    if (mapping.debit === "" && mapping.kredit === "" && mapping.jumlah === "") {
      toast.error("Petakan minimal kolom Debit/Kredit atau Jumlah"); return;
    }
    setLoading(true);
    try {
      const r = await api.post("/import/mutasi", {
        csv_text: csvText, mapping, akun_id: akunId,
        kategori_masuk: katMasuk, kategori_keluar: katKeluar,
        jenis_penghasilan: jenis, perlakuan_pajak: perlakuan,
      });
      setResult(r.data);
      toast.success(`Impor selesai: ${r.data.created_pemasukan} masuk, ${r.data.created_pengeluaran} keluar`);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const colOptions = preview ? preview.headers.map((h, i) => ({ v: String(i), l: `${i + 1}. ${h}` })) : [];

  return (
    <>
      <PageHeader title="Impor Mutasi Bank (CSV)" subtitle="Unggah rekening koran/mutasi CSV untuk mencatat pemasukan & pengeluaran sekaligus." />
      <div className="p-4 md:p-8 space-y-6 max-w-4xl">
        <div className="card-flat p-4 flex gap-3 border-l-4 border-l-blue-500">
          <Info size={18} className="text-blue-600 shrink-0" />
          <div className="text-sm text-slate-600">
            Ekspor mutasi dari internet/mobile banking ke <b>CSV</b>, lalu unggah di sini. Baris <b>Kredit</b> (uang masuk)
            menjadi <b>Pemasukan</b>, baris <b>Debit</b> (uang keluar) menjadi <b>Pengeluaran</b>, keduanya terhubung ke akun terpilih.
            Transfer antar rekening sendiri sebaiknya dihapus/dikelompokkan agar tidak dihitung sebagai penghasilan.
          </div>
        </div>

        {/* Step 1: upload */}
        <div className="card-flat p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Langkah 1 — Unggah File CSV</div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50" data-testid="import-file-label">
              <UploadCloud size={16} /> Pilih File CSV
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="import-file-input" />
            </label>
            {fileName && <span className="text-sm text-slate-600">{fileName}</span>}
            <Button variant="outline" onClick={doPreview} disabled={!csvText} className="rounded-sm" data-testid="import-preview-btn"><FileUp size={14} className="mr-1" /> Baca & Pratinjau</Button>
          </div>
        </div>

        {preview && (
          <>
            {/* Step 2: mapping */}
            <div className="card-flat p-5 space-y-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Langkah 2 — Petakan Kolom</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FIELD_LABELS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label>{f.label}</Label>
                    <Select value={mapping[f.key] || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid={`map-${f.key}`}><SelectValue placeholder="— tidak dipakai —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— tidak dipakai —</SelectItem>
                        {colOptions.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto border-t border-slate-100 pt-3">
                <div className="text-xs text-slate-400 mb-2">Contoh {Math.min(preview.sample.length, 10)} baris pertama:</div>
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-left text-slate-500">{preview.headers.map((h, i) => <th key={i} className="p-2 border-b">{h}</th>)}</tr></thead>
                  <tbody>{preview.sample.map((row, ri) => (
                    <tr key={ri} className="border-b border-slate-100">{row.map((c, ci) => <td key={ci} className="p-2 font-mono">{c}</td>)}</tr>
                  ))}</tbody>
                </table>
              </div>
            </div>

            {/* Step 3: settings */}
            <div className="card-flat p-5 space-y-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Langkah 3 — Tujuan & Klasifikasi</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Akun / Dompet Tujuan *</Label>
                  <Select value={akunId} onValueChange={setAkunId}>
                    <SelectTrigger data-testid="import-akun"><SelectValue /></SelectTrigger>
                    <SelectContent>{akun.map((a) => <SelectItem key={a.id} value={a.id}>{a.nama}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Kategori untuk Pemasukan</Label>
                  <Select value={katMasuk || "none"} onValueChange={(v) => setKatMasuk(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="import-kat-masuk"><SelectValue placeholder="(kosong)" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">(kosong)</SelectItem>{katMasukList.map((k) => <SelectItem key={k.id} value={k.nama}>{k.nama}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Kategori untuk Pengeluaran</Label>
                  <Select value={katKeluar || "none"} onValueChange={(v) => setKatKeluar(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="import-kat-keluar"><SelectValue placeholder="(kosong)" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">(kosong)</SelectItem>{katKeluarList.map((k) => <SelectItem key={k.id} value={k.nama}>{k.nama}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Perlakuan Pajak (Pemasukan)</Label>
                  <Select value={perlakuan} onValueChange={setPerlakuan}>
                    <SelectTrigger data-testid="import-perlakuan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bukan_objek">Bukan Objek Pajak (default — reklasifikasi nanti)</SelectItem>
                      <SelectItem value="progresif">Objek Progresif</SelectItem>
                      <SelectItem value="final">Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500">Default pemasukan ditandai <b>Bukan Objek</b> agar impor tidak langsung menaikkan pajak. Anda bisa mereklasifikasi tiap transaksi setelah impor.</p>
              <Button onClick={doImport} disabled={loading} className="brand-btn rounded-sm" data-testid="import-run-btn">
                {loading ? "Mengimpor..." : `Impor ${preview.total_rows} Baris`}
              </Button>
            </div>
          </>
        )}

        {result && (
          <div className="card-flat p-5 border-l-4 border-l-emerald-500 space-y-2" data-testid="import-result">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 size={18} /> Impor Selesai</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-slate-500">Pemasukan dibuat</div><div className="font-mono text-lg">{result.created_pemasukan}</div></div>
              <div><div className="text-slate-500">Pengeluaran dibuat</div><div className="font-mono text-lg">{result.created_pengeluaran}</div></div>
              <div><div className="text-slate-500">Total Masuk</div><div className="font-mono text-lg text-emerald-700">{rupiah(result.total_masuk)}</div></div>
              <div><div className="text-slate-500">Total Keluar</div><div className="font-mono text-lg text-rose-700">{rupiah(result.total_keluar)}</div></div>
            </div>
            {result.skipped > 0 && <div className="text-xs text-amber-600">{result.skipped} baris dilewati (tanggal/nominal tidak valid).</div>}
          </div>
        )}
      </div>
    </>
  );
}
